// PWA PANEL PLATFORM (`/platform/admin`) — helper murni: manifest, scope
// service worker, dan aturan pembersihan SW lama di origin platform.
//
// Kenapa PWA platform TERPISAH dari PWA toko:
//  • Origin platform (kelolify.com) tak boleh memakai `/sw.js` toko — ikon,
//    halaman offline, dan aturan cache-nya milik storefront. Komentar lama di
//    `ServiceWorkerRegister` menjelaskan kenapa SW toko yang terlanjur
//    ter-register di apex harus dicabut.
//  • SW platform hanya mengontrol `/platform/` (bukan "/"), jadi situs jualan
//    di `/` dan katalog reseller `/toko/<kode>` tetap tanpa service worker.
//  • `/platform/login` ikut di dalam scope: sesi kedaluwarsa dialihkan ke
//    sana, dan di luar scope aplikasi terpasang akan membuka bilah browser.

import { PLATFORM_ICON_192, PLATFORM_ICON_512 } from "@/lib/platformIcons";

/** Scope SW + manifest platform. Garis miring di ujung wajib (aturan scope SW). */
export const PLATFORM_PWA_SCOPE = "/platform/";

/** Halaman pembuka aplikasi terpasang. */
export const PLATFORM_PWA_START_URL = "/platform/admin";

/** Skrip SW khusus panel platform (berkas statis di `public/`). */
export const PLATFORM_SW_URL = "/sw-platform.js";

/** Rute manifest platform — di akar supaya tak ikut pindaian `src/app/platform`. */
export const PLATFORM_MANIFEST_PATH = "/manifest.platform.webmanifest";

/** Awalan nama cache milik SW platform; cache lain di origin platform dibuang. */
export const PLATFORM_CACHE_PREFIX = "kelolify-platform-";

/** Ikon maskable platform (latar penuh, monogram di zona aman 80%). */
export const PLATFORM_MASKABLE_192 = "/icons/platform-maskable-192.png";
export const PLATFORM_MASKABLE_512 = "/icons/platform-maskable-512.png";

/** Navy Kelolify — kembar `--theme-primary` di `body:has([data-platform-page])`. */
export const PLATFORM_THEME_COLOR = "#27228F";

/**
 * Kunci "popup pasang sudah ditutup" (localStorage). Prefiks `mitrahub-platform-`
 * mengikuti kunci platform yang sudah ada (`mitrahub-platform-visitor-id`) —
 * terpisah dari `galea-install-dismissed` milik popup toko.
 */
export const PLATFORM_INSTALL_DISMISS_KEY = "mitrahub-platform-install-dismissed";

/** Jeda sebelum popup pasang muncul lagi sesudah ditutup. */
export const PLATFORM_INSTALL_DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

export type PlatformManifest = {
  id: string;
  name: string;
  short_name: string;
  description: string;
  lang: string;
  start_url: string;
  scope: string;
  display: "standalone";
  theme_color: string;
  background_color: string;
  icons: { src: string; sizes: string; type: string; purpose: string }[];
  shortcuts: { name: string; short_name: string; url: string; icons: { src: string; sizes: string; type: string }[] }[];
};

/** Manifest PWA panel platform. Statis: tak ada merek toko di dalamnya. */
export function buildPlatformManifest(): PlatformManifest {
  const shortcutIcon = [{ src: PLATFORM_ICON_192, sizes: "192x192", type: "image/png" }];
  return {
    id: "/platform/admin?app=platform",
    name: "Kelolify Platform",
    short_name: "Kelolify",
    description: "Panel pengelola platform Kelolify",
    lang: "id",
    start_url: PLATFORM_PWA_START_URL,
    scope: PLATFORM_PWA_SCOPE,
    display: "standalone",
    theme_color: PLATFORM_THEME_COLOR,
    background_color: "#F5F5F5",
    icons: [
      { src: PLATFORM_ICON_192, sizes: "192x192", type: "image/png", purpose: "any" },
      { src: PLATFORM_ICON_512, sizes: "512x512", type: "image/png", purpose: "any" },
      { src: PLATFORM_MASKABLE_192, sizes: "192x192", type: "image/png", purpose: "maskable" },
      { src: PLATFORM_MASKABLE_512, sizes: "512x512", type: "image/png", purpose: "maskable" },
    ],
    shortcuts: [
      { name: "Tagihan Langganan", short_name: "Tagihan", url: "/platform/admin/tagihan", icons: shortcutIcon },
      { name: "Notifikasi", short_name: "Notifikasi", url: "/platform/admin/notifikasi", icons: shortcutIcon },
      { name: "Toko", short_name: "Toko", url: "/platform/admin/stores", icons: shortcutIcon },
    ],
  };
}

/** Halaman yang memakai PWA platform: `/platform/...` (bukan situs jualan di `/`). */
export function isPlatformPwaPath(pathname: string | null | undefined): boolean {
  return typeof pathname === "string" && pathname.startsWith(PLATFORM_PWA_SCOPE);
}

/**
 * Registrasi SW yang dicabut di origin platform: SEMUA kecuali milik panel
 * platform (scope `<origin>/platform/`). Dulu semuanya dicabut di setiap muat
 * halaman — aturan itu membunuh SW platform sebelum sempat menerima push.
 */
export function shouldUnregisterOnPlatformHost(scope: string, origin: string): boolean {
  return scope !== `${origin.replace(/\/+$/, "")}${PLATFORM_PWA_SCOPE}`;
}

/** Cache yang dibuang di origin platform: semua kecuali milik SW platform. */
export function shouldDeleteCacheOnPlatformHost(cacheName: string): boolean {
  return !cacheName.startsWith(PLATFORM_CACHE_PREFIX);
}

/** Popup pasang ditutup belum lewat jedanya? `raw` = isi localStorage. */
export function platformInstallDismissedRecently(raw: string | null, now: number): boolean {
  const t = Number(raw);
  return Number.isFinite(t) && t > 0 && now - t < PLATFORM_INSTALL_DISMISS_MS;
}

/**
 * Jenis petunjuk pasang dari user agent:
 *  • "ios"    — iPhone/iPad: tak ada event pasang; sejak iOS 16.4 semua browser
 *               bisa lewat Bagikan → Tambah ke Layar Utama, jadi tampilkan langkahnya.
 *  • "native" — selainnya: tunggu `beforeinstallprompt` (Chrome/Edge/Samsung).
 *
 * iPadOS modern mengaku "Macintosh"; dibedakan dari Mac lewat layar sentuh.
 */
export function platformInstallMode(userAgent: string, maxTouchPoints = 0): "ios" | "native" {
  if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
  if (/macintosh/i.test(userAgent) && maxTouchPoints > 1) return "ios";
  return "native";
}
